import React, { useState, useEffect } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Legend, Filler } from "chart.js";
import { Chart } from "react-chartjs-2";
import { Tooltip } from "react-tooltip";
import { Info } from "lucide-react";
import Footer from '../components/Footer';
import LandingSection from "../components/LandingSection";
import "../styles/UtahAtAGlance.css";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Legend, Filler);

const PopulationDemography = () => {
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        // Simulate fetching data from Population.json
        const fetchData = async () => {
            try {
                // const response = await fetch("/Population.json");
                const response = await fetch(`${process.env.PUBLIC_URL}/Population.json`);

                const data = await response.json();
                setChartData(data);
                setLoading(false);
            } catch (error) {
                console.error("Error loading population data:", error);
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Chart options
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top",
                labels: {
                    font: {
                        family: "JetBrains Mono",
                        size: 14,
                    },
                    padding: 20,
                    usePointStyle: true,
                },
            },
            title: {
                display: true,
                text: "Utah Population Distribution by Age Group (2025)",
                font: {
                    family: "JetBrains Mono",
                    size: 18,
                    weight: "bold",
                },
                padding: {
                    top: 10,
                    bottom: 30,
                },
            },
            tooltip: {
                callbacks: {
                    label: (context) => `${context.dataset.label}: ${context.raw.toLocaleString()}`,
                },
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: "Age Groups(in years)",
                    font: {
                        family: "JetBrains Mono",
                        size: 14,
                        weight: "bold",
                    },
                },
                grid: {
                    display: true,
                },
            },
            y: {
                title: {
                    display: true,
                    text: "Population(in numbers)",
                    font: {
                        family: "JetBrains Mono",
                        size: 14,
                        weight: "bold",
                    },
                },
                beginAtZero: true,
                ticks: {
                    callback: (value) => value.toLocaleString(),
                },
            },
        },
        interaction: {
            mode: "index",
            intersect: false,
        },
    };

    // Stats data
    const stats = [
        {
            title: "Total Population",
            value: "3.45M",
            description: "Estimated January 2025",
        },
        {
            title: "Median Age",
            value: "31.7 years",
            description: "Statewide average",
        },
        {
            title: "Age Dependency Ratio",
            value: "66.4",
            description: "Population (0-14 & 65+) / Population (15-64)",
            tooltip: "100 x (Population (0 to 14) + Population (65+)) / Population (15 to 64)",
        },
    ];

    const genderData = [
        { gender: "Male", count: "1,686,560", percentage: "50.63%" },
        { gender: "Female", count: "1,644,620", percentage: "49.37%" },
    ];

    const ageGroups = [
        { range: "0-14", count: "772,844", percentage: "23.2%" },
        { range: "15-64", count: "2,387,060", percentage: "71.6%" },
        { range: "65+", count: "387,028", percentage: "11.6%" },
    ];

    return (
        <>
            <LandingSection
                data={{ imageName: "login.webp", title: "Utah Demographics" }}
            />
            <div className="utah-at-a-glance-section">
                <div className="utah-at-a-glance-intro">
                    <div className="utah-at-a-glance-intro-sec1">
                        <h2>
                            Utah's Population Story: Numbers That Define Our Community
                        </h2>
                    </div>
                    <div className="utah-at-a-glance-intro-sec2">
                        <p>
                            {/* Explore the heart of Utah—its people, places, economy, and
                            culture. From county-level insights to infrastructure and
                            lifestyle, this is your one-stop overview of what defines the
                            Beehive State. */}
                            Explore the diverse tapestry of Utah's population through comprehensive demographic data.
                            Discover age distributions, gender ratios, growth trends, and key statistics that reveal
                            the unique characteristics of our state's residents.
                        </p>
                    </div>
                </div>
            </div>
            <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
                {/* Header */}
                {/* <header className="bg-blue-900 text-white py-8 px-4 sm:px-6 lg:px-8">
                    <div className="max-w-7xl mx-auto text-center">
                        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Utah Demographics</h1>
                        <p className="text-lg sm:text-xl text-blue-100">
                            Comprehensive population statistics and trends for Utah (2025)
                        </p>
                    </div>
                </header> */}

                {/* Main Content */}
                <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {stats.map((stat, index) => (
                            <div
                                key={index}
                                
                               className="bg-white rounded-xl p-6 border border-blue-800 shadow-[0_8px_24px_rgba(37,99,235,0.6)] hover:shadow-[0_12px_32px_rgba(30,64,175,0.8)] transition-shadow duration-300"


                            >
                                <div className="flex justify-between items-start">
                                    <h3 className="text-lg font-semibold text-gray-700">{stat.title}</h3>
                                    {stat.tooltip && (
                                        <>
                                            <Info
                                                size={18}
                                                className="text-blue-500 cursor-pointer"
                                                data-tooltip-id={`tooltip-${index}`}
                                                data-tooltip-content={stat.tooltip}
                                            />
                                            <Tooltip id={`tooltip-${index}`} />
                                        </>
                                    )}
                                </div>
                                <p className="text-3xl font-bold text-blue-800 my-2">{stat.value}</p>
                                <p className="text-sm text-gray-500">{stat.description}</p>
                            </div>
                        ))}
                    </div>

                    {/* Chart Section */}
                    <div className="bg-white rounded-xl border border-blue-800 shadow-[0_8px_24px_rgba(37,99,235,0.6)] hover:shadow-[0_12px_32px_rgba(30,64,175,0.8)] transition-shadow duration-300 p-6 mb-8">
                        {/* {loading ? (
                            <div className="h-96 flex items-center justify-center">
                                <div className="animate-pulse text-gray-500">Loading chart data...</div>
                            </div>
                        ) : (
                            <div className="h-[450px] sm:h-[500px] md:h-[550px] lg:h-[600px]">
                                <Chart type="bar" data={chartData} options={chartOptions} />
                            </div>
                        )} */}

                        {loading || !chartData || !chartData.labels ? (
  <div className="h-96 flex items-center justify-center">
    <div className="animate-pulse text-gray-500">Loading chart data...</div>
  </div>
) : (
  <div className="h-[450px] sm:h-[500px] md:h-[550px] lg:h-[600px] ">
    <Chart type="bar" data={chartData} options={chartOptions} />
  </div>
)}

                    </div>

                    {/* Data Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Gender Distribution */}
                        <div className=" bg-white rounded-xl p-6 border border-blue-800 shadow-[0_8px_24px_rgba(37,99,235,0.6)] hover:shadow-[0_12px_32px_rgba(30,64,175,0.8)] transition-shadow duration-300">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Gender Distribution</h2>
                            <div className="space-y-4">
                                {genderData.map((item, index) => (
                                    <div key={index}>
                                        <div className="flex justify-between mb-1">
                                            <span className="font-medium">{item.gender}</span>
                                            <span className="text-gray-600">{item.percentage}</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div
                                                className={`h-2.5 rounded-full ${item.gender === "Male" ? "bg-[#26689B]" : "bg-[#cb0c0c]"}`}
                                                style={{ width: item.percentage }}
                                            ></div>
                                        </div>
                                        <div className="text-right text-sm text-gray-500 mt-1">{item.count}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Age Group Distribution */}
                        <div className="bg-white rounded-xl p-6 border border-blue-800 shadow-[0_8px_24px_rgba(37,99,235,0.6)] hover:shadow-[0_12px_32px_rgba(30,64,175,0.8)] transition-shadow duration-300">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Age Group Distribution</h2>
                            <div className="space-y-4">
                                {ageGroups.map((group, index) => (
                                    <div key={index}>
                                        <div className="flex justify-between mb-1">
                                            <span className="font-medium">{group.range} years</span>
                                            <span className="text-gray-600">{group.percentage}</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                                            <div
                                                className="h-2.5 rounded-full bg-[#26859b]"
                                                style={{ width: group.percentage }}
                                            ></div>
                                        </div>
                                        <div className="text-right text-sm text-gray-500 mt-1">{group.count}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-100">
                        <h2 className="text-xl font-bold text-gray-800 mb-2">About Utah's Population</h2>
                        <p className="text-gray-700 mb-4">
                            Utah is one of the fastest-growing states in the U.S. with a young population and high birth rate.
                            The state's median age is significantly lower than the national average.
                        </p>
                        <div className="text-sm text-gray-500">
                            <p>Note: Population data are estimated as of January 2025.</p>
                            <p className="mt-2">
                                Source:{" "}
                                <a
                                    href="https://worldpopulationreview.com/states/utah"
                                    className="text-blue-600 hover:underline"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    World Population Review
                                </a>
                            </p>
                        </div>
                    </div>
                </main>
            </div>
            <Footer />
        </>
    );
}

export default PopulationDemography
